import { Alert, Button, Group, Paper, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconBuilding, IconFile, IconInfoCircle, IconSettings } from "@tabler/icons-react";
import React from "react";
import { Link } from "react-router-dom";
import planvisningBackground from "../../../../../assets/planvisning.png";

type McbimFloorplanInfoVariant = "notActivated" | "noFiles" | "noSpaces";

const INFO_CONTENT: Record<
  McbimFloorplanInfoVariant,
  {
    title: string;
    description: string;
  }
> = {
  notActivated: {
    title: "BIM er ikke aktivert",
    description:
      "For å komme i gang må prosjektet ha IFC-filer tilgjengelig på Multiconsults ACC-konto. Filene legges til og håndteres under BIM-innstillingene.",
  },
  noSpaces: {
    title: "Ingen romdata funnet",
    description:
      "IFC-filer er synkronisert fra Autodesks ACC, men det finnes ingen romdata å vise i plantegningen ennå. Kontroller at de valgte IFC-filene inneholder IfcSpace-objekter og at synkroniseringsjobben har kjørt ferdig.",
  },
  noFiles: {
    title: "Ingen filer lastet i MCBIM",
    description:
      "IFC-tilkoblingen til Autodesks ACC er koblet opp, men det finnes ingen filer lastet i MCBIM ennå. Legg til IFC-filer og start lasting for å komme i gang med plantegningen.",
  },
};

const McbimFloorplanInfo: React.FC<{ projectId: string; variant: McbimFloorplanInfoVariant }> = ({
  projectId,
  variant,
}) => {
  const content = INFO_CONTENT[variant];

  if (variant === "notActivated") {
    return (
      <Paper
        withBorder
        p="xl"
        radius="md"
        style={{
          position: "relative",
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.9) 45%, rgba(255,255,255,0.24) 100%), url(${planvisningBackground})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <Stack gap="md" maw={660} p="xl" style={{ position: "relative", zIndex: 1 }}>
          <Group gap="sm" align="center">
            <ThemeIcon color="mc-primary" variant="light" size="lg">
              <IconBuilding size={22} />
            </ThemeIcon>
            <Title order={3}>{content.title}</Title>
          </Group>

          <Text c="dimmed">
            BIM bruker romdata fra IFC-filer til å vise rom som en interaktiv plantegning. Her kan du se etasjer, rom,
            koblede saker og romobjekter direkte i saksregisteret.
          </Text>

          <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
            Denne visningen er foreløpig tilpasset bygninger med rom og etasjer, og er ikke tilpasset
            infrastrukturmodeller.
          </Alert>

          <Group gap="sm" align="flex-start" wrap="nowrap">
            <ThemeIcon color="gray" variant="light" mt={2}>
              <IconFile size={18} />
            </ThemeIcon>
            <Text size="sm">{content.description}</Text>
          </Group>

          <Group>
            <Button
              component={Link}
              to={`/project/${projectId}/settings/mcbim`}
              color="mc-primary"
              leftSection={<IconSettings size={16} />}
            >
              Gå til BIM-innstillinger
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  return (
    <Alert
      color="blue"
      variant="light"
      icon={<IconInfoCircle size={18} />}
      title={content.title}
      styles={{ body: { width: "100%" } }}
    >
      <Group justify="space-between" align="flex-start" gap="md">
        <Stack gap={4} style={{ flex: 1 }}>
          <Text size="sm">{content.description}</Text>
        </Stack>
        <Group>
          <Button
            component={Link}
            to={`/project/${projectId}/settings/mcbim`}
            color="mc-primary"
            size="xs"
            variant="light"
            leftSection={<IconSettings size={16} />}
          >
            Legg til data
          </Button>
        </Group>
      </Group>
    </Alert>
  );
};

export default McbimFloorplanInfo;
